<template>
  <overlay-template
    :title="'Sprint rate'"
    :primary-number-title="'Minimum'"
    :primary-number="rate.sprintRate"
    :primary-number-unit="'/person/client (1/' + rate.goal.clientLoad + ' capacity)'"
    :secondary-number="rate.sprintBreakEven"
    :good-for="[
      'Presenting a final estimating unit to clients and teams (flat sprint rate × # of expected sprints)',
      'Planning and scoping the length of time a project will take',
      'Accounting for buffer in projects like feedback and turnarounds, project setup and meetings, flexibility in agile projects',
      'Planning your employee‘s capacity',
      'Adding blocks of maintenance time',
      'Scoping for more project based or unscoped maintenance work',
      'Ties more closely with monthly financial cycle than weeks or days (better predictive revenue forecasting)'
    ]"
    :not-good-for="[
      'Scoping the length of each task (stick to days for estimating cards)',
      'Big picture planning (quarterly or annual)',
      'Forecasting'
    ]"
  >
    <div slot="intro">
      <p>Your sprint rate is usually the amount you charge for two weeks of work, although sprints can be shorter or longer if needed.</p>
      <p>A two-week sprint is a digestible unit for clients, and aligns particularly well with product companies that develop in sprints. Sprint rates make it easier to set expectations with clients as you can alert them in advance if they need to pull back on scope or add another sprint to complete prioritized features (tacking on an additional sprint building in a range for a minimum viable product is easier to the scope and digest than guessing how many more hours to get to the finish line). Break your sprint rates into threes: assuming each employee manages 3 clients on average at once. Otherwise, your sprint rate will be higher for managing fewer more complex projects at once.</p>
    </div>
    <p slot="formula">{{ rate.goal.incoming | currency }} total annual revenue &divide; ({{ rate.goal.workingWeeks }} average working weeks &divide; 2) &divide; {{ rate.goal.billableEmployees }} billable employees &times; 1/{{ rate.goal.clientLoad }} capacity (client load) = <strong>{{ rate.sprintRate | currency }}/sprint/person/client</strong></p>
    <p slot="break-even-formula">{{ rate.goal.outgoing | currency }} total annual expenses &divide; ({{ rate.goal.workingWeeks }} average working weeks &divide; 2) &divide; {{ rate.goal.billableEmployees }} billable employees &times; 1/{{ rate.goal.clientLoad }} capacity (client load) = <strong>{{ rate.sprintBreakEven | currency }}/sprint/person/client</strong></p>
    <div
      v-if="rate.sprintRate"
      slot="charts"
      class="grid-column-span-3_sm grid-column-span-2_lg">
      <div class="reverse p2 c1 group">
        <h2>Ballpark estimator</h2>
        <p class="measure left">Calculate a high-level estimate for your project based on people needed, duration, and capacity. Then refine your estimate based on a more detailed scope, risks, and the value you can return.</p>
        <p class="mb2 align-right"><span class="font-size-8 extra-condensed price">{{ sprints * people * capacity * rate.sprintRate | currency }}</span>&nbsp;<span
          v-if="rate.sprintBreakEven"
          class="font-size-2 bold dark-red-300-text">{{ sprints * people * capacity * rate.sprintBreakEven | currency }}</span> <span class="font-size-1 dark-red-300-text m0 semibold">break even</span></p>
        <lw-range-input
          :label="peopleLabel"
          :min="1"
          :max="rate.goal.billableEmployees"
          :step="1"
          :class="'mb2 clear'"
          v-model="people"
        />
        <lw-range-input
          :label="'Average ' + Math.round(capacity / rate.goal.clientLoad * 100) + '% allocation to project per person'"
          :min="1"
          :max="rate.goal.clientLoad"
          :step="1"
          :class="'mb2'"
          v-model="capacity"
        />
        <lw-range-input
          :label="sprints + ' sprint project'"
          :min="1"
          :max="rate.goal.workingSprints"
          :step="1"
          v-model="sprints"
        />
      </div>
    </div>
  </overlay-template>
</template>

<script>
import OverlayTemplate from './OverlayTemplate.vue'
import RangeInput from '../../livewires/RangeInput.vue'

export default {
  components: {
    overlayTemplate: OverlayTemplate,
    lwRangeInput: RangeInput
  },
  data () {
    return {
      page: this.$route.params.id,
      people: 1,
      sprints: 1,
      capacity: 1
    }
  },
  computed: {
    rate () {
      return this.$store.getters.rateCard(this.page)
    },
    peopleLabel () {
      const label = this.people > 1 ? ' people' : ' person'
      return 'Average ' + this.people + label + ' on the project'
    }
  }
}
</script>
